import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, FolderInput, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { deleteProject, duplicateProject, listFolders, updateProject } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

function TextDialog({ open, title, description, label, initial, suggestions, onClose, onSave, testId }) {
  const [value, setValue] = useState(initial || "");
  useEffect(() => { if (open) setValue(initial || ""); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testId}-dialog`} className="border-white/10 bg-[#0a0d1c] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          {description ? <DialogDescription className="text-white/55">{description}</DialogDescription> : null}
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(value.trim()); }}>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">{label}</span>
            <input autoFocus data-testid={`${testId}-input`} value={value} onChange={(e) => setValue(e.target.value)} maxLength={label === "Album" ? 60 : 120} list={suggestions ? `${testId}-list` : undefined} className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-sm text-white focus:border-blue-400/60 focus:outline-none" />
            {suggestions ? <datalist id={`${testId}-list`}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist> : null}
          </label>
          <DialogFooter className="mt-5 gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
            <button type="submit" data-testid={`${testId}-save`} className="rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2 text-sm font-semibold">Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const btn = "inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white";

/** Rename · Move to folder · Duplicate · Delete — shared by the project page and the library list. */
export default function ProjectActions({ project, onChanged, onDeleted, compact = false }) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [folders, setFolders] = useState([]);
  const id = project.id;

  const openMove = () => { setDialog("move"); listFolders().then((f) => setFolders(f.map((x) => x.name))).catch(() => setFolders([])); };
  const save = async (patch, msg) => {
    try { const p = await updateProject(id, patch); setDialog(null); toast.success(msg); onChanged?.(p); }
    catch (e) { toast.error("Couldn't save", { description: safeApiError(e) }); }
  };
  const duplicate = async () => {
    try { const p = await duplicateProject(id); toast.success("Project duplicated", { description: p.title }); onChanged?.(null); if (!compact) navigate(`/create/project/${p.id}`); }
    catch (e) { toast.error("Couldn't duplicate", { description: safeApiError(e) }); }
  };
  const remove = async () => {
    try { await deleteProject(id); toast.success("Moved to Deleted", { description: "Restore it from My Music Library → Deleted." }); onDeleted?.(); }
    catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); }
  };

  return (
    <div data-testid={`project-actions-${id}`} className="flex flex-wrap gap-1.5">
      <button type="button" data-testid={`project-rename-${id}`} onClick={() => setDialog("rename")} className={btn}><Pencil className="h-3 w-3" />{compact ? "" : " Rename"}</button>
      <button type="button" data-testid={`project-move-${id}`} onClick={openMove} className={btn}><FolderInput className="h-3 w-3" />{compact ? "" : " Album"}</button>
      <button type="button" data-testid={`project-duplicate-${id}`} onClick={duplicate} className={btn}><Copy className="h-3 w-3" />{compact ? "" : " Duplicate"}</button>
      <button type="button" data-testid={`project-delete-${id}`} onClick={remove} className={`${btn} hover:text-rose-300`}><Trash2 className="h-3 w-3" />{compact ? "" : " Delete"}</button>
      <TextDialog open={dialog === "rename"} testId="rename" title="Rename project" label="Title" initial={project.title} onClose={() => setDialog(null)} onSave={(v) => save({ title: v || "Untitled" }, "Renamed")} />
      <TextDialog open={dialog === "move"} testId="folder" title="Add to an album or collection" description="Type a new album / collection name or pick an existing one. Leave empty to take it out." label="Album" initial={project.folder} suggestions={folders} onClose={() => setDialog(null)} onSave={(v) => save({ folder: v }, v ? `Added to ${v}` : "Removed from the album")} />
    </div>
  );
}
